/* eslint-disable @typescript-eslint/no-unused-vars */
import { EventEmitter } from 'events';
import LocalMediaUtils from './LocalMediaUtils';
import {
  RealtimeKitMediaStreamConstraints,
  MediaPermission,
} from './LocalMediaInterfaces';
import type { MediaStreamTrack } from '@cloudflare/react-native-webrtc';
import type { MediaDeviceInfo } from './LocalMediaInterfaces';
import {
  AppState,
  NativeEventEmitter,
  NativeEventSubscription,
  NativeModules,
  PermissionsAndroid,
  Platform,
} from 'react-native';
import BackgroundTimer from './BackgroundHandler';
import NativeAudioManager from './NativeAudioManager';
import { setupPermissions } from './PermissionHandler';

const { RTKHelper, BroadcastEventEmitter } = NativeModules;

export const broadcastEmitter = new NativeEventEmitter(BroadcastEventEmitter);

// eslint-disable-next-line no-shadow
export enum MediaEvents {
  AUDIO_TRACK_CHANGE,
  VIDEO_TRACK_CHANGE,
  SCREENSHARE_TRACK_CHANGE,
  SCREENSHARE_ENDED,
  DEVICE_CHANGE,
  SCREENSHARE_STARTED,
  MEDIA_PERMISSION_UPDATE,
  DEVICE_LIST_UPDATED,
}

function checkIfAudioTrackIsSilent(track: MediaStreamTrack) {
  //TODO
  return !!track;
}

export default class LocalMediaHandler extends EventEmitter {
  #localMediaUtils: LocalMediaUtils;

  #appState: string;

  #appStateSubscription: NativeEventSubscription | undefined;

  #interval: number;

  #screenShareState: 'STARTED' | 'STOPPED' | 'PENDING' | 'NOT_STARTED';

  public audioTrack: MediaStreamTrack;

  public rawAudioTrack: MediaStreamTrack;

  public videoTrack: MediaStreamTrack;

  public rawVideoTrack: MediaStreamTrack;

  public screenShareTracks: {
    audio?: MediaStreamTrack;
    video?: MediaStreamTrack;
  };

  public audioEnabled: boolean;

  public videoEnabled: boolean;

  public screenShareEnabled: boolean;

  private _pendingScreenShare = false;

  public currentDevices: {
    audio?: MediaDeviceInfo;
    video?: MediaDeviceInfo;
    speaker?: MediaDeviceInfo;
  };

  public permissions: {
    audio?: MediaPermission;
    video?: MediaPermission;
  };

  #deviceRemovalTimeout: NodeJS.Timeout | null = null;

  #broadcastEmitter: NativeEventEmitter;

  #broadcastStartedSubscription: NativeEventSubscription;

  #broadcastStoppedSubscription: NativeEventSubscription;

  _handleAppStateChange = (nextAppState) => {
    if (
      this.#appState.match(/inactive|background/) &&
      nextAppState === 'active'
    ) {
      BackgroundTimer.clearInterval(this.#interval);
      this.#interval = null;
      if (this.videoTrack) {
        this.videoTrack.enabled = false;
        setTimeout(() => {
          if (this.videoTrack) {
            this.videoTrack.enabled = true;
          }
        }, 100);
      }
    } else {
      if (this.#interval === null) {
        if (this.screenShareEnabled || this._pendingScreenShare) {
          this.configureForeground();
        }
      }
      this.#interval = BackgroundTimer.setInterval(() => {}, 1000);
      this.#appState = nextAppState;
    }
  };

  private async configureForeground() {
    if (Platform.OS !== 'android') return;
    const val = await RTKHelper.isForegroundServiceRunning();
    if (!val) {
      if ((Platform.Version as number) >= 33) {
        const res = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS
        );
        if (res !== PermissionsAndroid.RESULTS.GRANTED) {
          console.warn('Permission required for screenshare notification');
        }
      }
      if ((Platform.Version as number) >= 26) {
        await RTKHelper.createNotificationChannel();
      }
      try {
        await RTKHelper.startService();
      } catch (e) {
        console.warn('Failed to start foreground service', e);
      }
    }
  }

  public constructor(localMediaUtils: LocalMediaUtils) {
    super();
    this.#localMediaUtils = localMediaUtils;
    this.#broadcastEmitter = broadcastEmitter;
    this.#appState = '';
    this.audioEnabled = true;
    this.videoEnabled = true;
    this.screenShareEnabled = false;
    this.permissions = { audio: 'NOT_REQUESTED', video: 'NOT_REQUESTED' };
    this.screenShareTracks = {
      audio: undefined,
      video: undefined,
    };
    this.currentDevices = {
      audio: undefined,
      video: undefined,
      speaker: undefined,
    };
    // LocalMediaUtils onDeviceChange with callback from native onAudioDeviceChanged events
    this.#localMediaUtils.onDeviceChange(async (changedDevices) => {
      await this.onDeviceChange(changedDevices, false);
    });
    if (Platform.OS === 'ios') {
      this.#broadcastStartedSubscription = this.#broadcastEmitter.addListener(
        'iOS_BroadcastStarted',
        () => {
          this.oniOSBroadcastStart();
        }
      );
      this.#broadcastStoppedSubscription = this.#broadcastEmitter.addListener(
        'iOS_BroadcastStopped',
        () => {
          this.oniOSBroadcastStop();
        }
      );
    }
    NativeAudioManager.start({ media: 'video' });
  }

  private async conditionallyRestartAudio() {
    if (!this.currentDevices.audio || !this.audioEnabled) {
      return;
    }
    await Promise.race([
      new Promise((resolve) =>
        this.audioTrack?.addEventListener('unmute', (e) => {
          resolve(e);
        })
      ),
      new Promise((resolve) => setTimeout(resolve, 50)),
    ]);
    const reacquireTrack = await this.shouldReacquireTrack('audio');

    if (reacquireTrack) {
      this.removeMediaStreamTrackListeners(this.audioTrack);
      await this.setupAudioStream(this.currentDevices.audio?.deviceId);
      this.addMediaStreamTrackListeners(this.audioTrack);
      this.emit('AUDIO_TRACK_CHANGE');
    }
  }

  private async conditionallyRestartVideo() {
    if (!this.currentDevices.video || !this.videoEnabled) return;

    await Promise.race([
      new Promise((resolve) =>
        this.videoTrack?.addEventListener('unmute', resolve)
      ),
      new Promise((resolve) => setTimeout(resolve, 50)),
    ]);

    const reacquireTrack = await this.shouldReacquireTrack('video');

    if (reacquireTrack) {
      this.removeMediaStreamTrackListeners(this.videoTrack);
      await this.setupVideoStream(this.currentDevices.video.deviceId);
      this.addMediaStreamTrackListeners(this.videoTrack);
      this.emit('VIDEO_TRACK_CHANGE');
    }
  }

  private async shouldReacquireTrack(kind: 'audio' | 'video') {
    const workaroundWebKitBug1208516 = false;
    let mediaStreamTrack: MediaStreamTrack;
    let wasEnabled = false;

    if (kind === 'audio') {
      mediaStreamTrack = this.audioTrack;
      wasEnabled = this.audioEnabled;
    } else if (kind === 'video') {
      mediaStreamTrack = this.videoTrack;
      wasEnabled = this.videoEnabled;
    }

    if (!mediaStreamTrack) return false;
    if (!this.rawAudioTrack || !this.audioTrack) return true;
    if (!this.rawVideoTrack || !this.videoTrack) return true;
    const isStopped = mediaStreamTrack.readyState === 'ended';

    const isInadvertentlyStopped =
      isStopped && wasEnabled && workaroundWebKitBug1208516;

    let isSilentAudio = false;
    if (kind === 'audio') {
      isSilentAudio = await checkIfAudioTrackIsSilent(this.audioTrack);
    }

    return mediaStreamTrack.muted || isInadvertentlyStopped || isSilentAudio;
  }

  public async removeDocumentEventListeners() {}

  private onUnmute(ev) {
    // log.info('Track unmuted', ev);
  }

  private removeMediaStreamTrackListeners(mediaStreamTrack: MediaStreamTrack) {
    if (!mediaStreamTrack) {
      return undefined;
    }

    const conditionallyRestart =
      mediaStreamTrack.kind === 'audio'
        ? this.conditionallyRestartAudio
        : this.conditionallyRestartVideo;
    mediaStreamTrack.removeEventListener('ended', conditionallyRestart);
    mediaStreamTrack.removeEventListener('mute', conditionallyRestart);
    mediaStreamTrack.removeEventListener('unmute', this.onUnmute);
    return mediaStreamTrack;
  }

  private addMediaStreamTrackListeners(mediaStreamTrack: MediaStreamTrack) {
    if (!mediaStreamTrack) {
      return null;
    }
    const conditionallyRestart =
      mediaStreamTrack.kind === 'audio'
        ? this.conditionallyRestartAudio
        : this.conditionallyRestartVideo;
    mediaStreamTrack.addEventListener('ended', conditionallyRestart);
    mediaStreamTrack.addEventListener('mute', conditionallyRestart);
    mediaStreamTrack.addEventListener('unmute', this.onUnmute);

    return mediaStreamTrack;
  }

  public async setupAudioStream(
    deviceId?: string,
    forceStopTrack: boolean = false
  ) {
    const audioDevices = await this.#localMediaUtils.getAudioDevices();

    if (!audioDevices.length) {
      return;
    }

    let audioDeviceId = deviceId;

    if (!audioDeviceId) {
      audioDeviceId = (await this.getOperationalMicDeviceId()).deviceId;
    }

    if (this.currentDevices.audio?.deviceId !== audioDeviceId) {
      this.currentDevices.audio = this.#localMediaUtils.getDeviceById(
        audioDeviceId,
        'audioinput'
      );
    }
    if (forceStopTrack) {
      this.audioTrack?.stop();
      this.rawAudioTrack?.stop();
    }
    try {
      this.rawAudioTrack = await LocalMediaUtils.getAudioTrack();
    } catch (err) {
      console.warn('Failed to acquire audio track: ', err);
      this.rawAudioTrack = undefined;
    }
    this.setAudioTrack(this.rawAudioTrack);
    this.addMediaStreamTrackListeners(this.audioTrack);
    this.emit('AUDIO_TRACK_CHANGE');
    this.setDevice(this.currentDevices.audio);

    /**
     * NOTE(roerohan): Firefox does not show device labels until permissions are granted.
     * So, we need to repopulate the devices once permission is granted.
     */
    if (this.audioTrack && this.permissions.audio === 'NOT_REQUESTED') {
      await this.#localMediaUtils.repopulateAvailableDevices();
    }

    // Force update audio devices on iOS after audio session is active
    // This ensures route change notifications start firing
    NativeAudioManager.forceUpdateAudioDevices();

    this.emit('DEVICE_CHANGE', {
      device: this.currentDevices.audio,
    });
  }

  private onVisibilityChange() {
    // pass
  }

  public async setupVideoStream(
    deviceId?: string,
    forceStopTrack: boolean = false
  ) {
    const videoDevices = await this.#localMediaUtils.getVideoDevices();
    if (!videoDevices.length) {
      return;
    }

    let videoDeviceId = deviceId;
    if (!videoDeviceId) {
      videoDeviceId = (await this.getOperationalWebcamDeviceId()).deviceId;
    }

    if (this.currentDevices.video?.deviceId !== videoDeviceId) {
      this.currentDevices.video = this.#localMediaUtils.getDeviceById(
        videoDeviceId,
        'videoinput'
      );
    }

    /**
     * NOTE(roerohan): this.videoEnabled is checked, so that upon device change,
     * the video track is not set when the user has turned off his camera.
     */
    if (this.videoEnabled) {
      const videoConstraints = LocalMediaUtils.getVideoConstraints({
        videoDeviceId,
      });
      if (!this.rawVideoTrack) {
        try {
          this.rawVideoTrack = await LocalMediaUtils.getVideoTrack(
            videoConstraints
          );
        } catch (err) {
          console.warn('Failed to acquire video track: ', err);
          this.rawVideoTrack = undefined;
        }
      }
      this.setVideoTrack(this.rawVideoTrack);
      this.addMediaStreamTrackListeners(this.videoTrack);
      this.emit('VIDEO_TRACK_CHANGE');
    }

    if (forceStopTrack) {
      this.videoTrack?.stop();
      this.rawVideoTrack?.stop();
    }
    /**
     * NOTE(roerohan): Firefox does not show device labels until permissions are granted.
     * So, we need to repopulate the devices once permission is granted.
     */
    if (this.videoTrack && this.permissions.video === 'NOT_REQUESTED') {
      await this.#localMediaUtils.repopulateAvailableDevices();
    }
    this.emit('DEVICE_CHANGE', {
      device: this.currentDevices.video,
    });
  }

  public async setupSpeaker(deviceId?: string) {
    const speakerDevices = await this.#localMediaUtils.getSpeakerDevices();
    if (!speakerDevices.length) {
      return;
    }

    let speakerDeviceId: string;

    if (deviceId) {
      // If explicit deviceId provided, use it
      speakerDeviceId = deviceId;
    } else {
      // Prefer current device if it's still available
      const currentDeviceStillAvailable = speakerDevices.find(
        (d) => d.deviceId === this.currentDevices.speaker?.deviceId
      );

      if (currentDeviceStillAvailable) {
        // Keep current device if it's still connected
        speakerDeviceId = this.currentDevices.speaker.deviceId;
      } else {
        // Otherwise, prefer Bluetooth > Wired > Speaker > Earpiece
        const bluetoothDevice = speakerDevices.find(
          (d) => d.deviceId === 'bluetooth'
        );
        const wiredDevice = speakerDevices.find((d) => d.deviceId === 'wired');
        const speakerDevice = speakerDevices.find(
          (d) => d.deviceId === 'speaker'
        );

        speakerDeviceId =
          bluetoothDevice?.deviceId ??
          wiredDevice?.deviceId ??
          speakerDevice?.deviceId ??
          speakerDevices[0].deviceId;
      }
    }

    if (this.currentDevices.speaker?.deviceId !== speakerDeviceId) {
      // Audio Input devices are same as output devices in mobile
      this.currentDevices.audio = this.#localMediaUtils.getDeviceById(
        speakerDeviceId,
        'audioinput'
      );
      this.currentDevices.speaker = this.currentDevices.audio;
    }
    await this.setDevice(this.currentDevices.audio);
    this.emit('DEVICE_CHANGE', {
      device: this.currentDevices.speaker,
    });
  }

  public async getOperationalWebcamDeviceId(): Promise<{
    isOperational: boolean;
    deviceId: string;
  }> {
    let videoDevices = await this.#localMediaUtils.getVideoDevices();
    if (!videoDevices.length) {
      return { isOperational: false, deviceId: null };
    }

    /*
            NOTE(ravindra-dyte): Usually external devices come at the bottom,
            By reversing the deviceList and breaking on first operational device
            We are giving preference to external device
        */

    videoDevices = [...videoDevices].reverse();

    let operationalDeviceId = null;
    for (
      let deviceIndex = 0;
      deviceIndex < videoDevices.length;
      deviceIndex += 1
    ) {
      try {
        operationalDeviceId = videoDevices[deviceIndex].deviceId;
        break;
      } catch (err) {}
    }

    return operationalDeviceId
      ? { isOperational: true, deviceId: operationalDeviceId }
      : { isOperational: false, deviceId: videoDevices[0].deviceId };
  }

  public async getOperationalMicDeviceId(): Promise<{
    isOperational: boolean;
    deviceId: string;
  }> {
    let audioDevices = await this.#localMediaUtils.getAudioDevices();
    if (!audioDevices.length) {
      return { isOperational: false, deviceId: null };
    }
    /*
            NOTE(ravindra-dyte): Usually external devices come at the bottom,
            By reversing the deviceList and breaking on first operational device
            We are giving preference to external device
        */

    audioDevices = [...audioDevices].reverse();
    let operationalDeviceId = null;

    for (
      let deviceIndex = 0;
      deviceIndex < audioDevices.length;
      deviceIndex += 1
    ) {
      try {
        const audioTrack = await LocalMediaUtils.getAudioTrack();

        const isSilent = await checkIfAudioTrackIsSilent(audioTrack);
        audioTrack.stop();
        audioTrack.release();
        if (!isSilent) {
          operationalDeviceId = audioDevices[deviceIndex].deviceId;
          break;
        }
      } catch (err) {
        console.warn('Failed to acquire audio track: ', err);
      }
    }

    return operationalDeviceId
      ? { isOperational: true, deviceId: operationalDeviceId }
      : { isOperational: false, deviceId: audioDevices[0].deviceId };
  }

  public async setupStreams({
    audio,
    video,
  }: {
    audio: boolean;
    video: boolean;
  }) {
    try {
      const permResult = await setupPermissions(audio, video);
      this.permissions.video = permResult.camera;
      this.permissions.audio = permResult.microphone;
    } catch (val) {
      this.permissions.video = val.camera ?? false;
      this.permissions.audio = val.microphone ?? false;
    }
    this.emit('MEDIA_PERMISSION_UPDATE', {
      kind: 'audio',
      message: this.permissions.audio,
    });

    this.emit('MEDIA_PERMISSION_UPDATE', {
      kind: 'video',
      message: this.permissions.video,
    });
    const constraints: RealtimeKitMediaStreamConstraints = {};
    if (!this.currentDevices) {
      this.currentDevices = {
        audio: undefined,
        video: undefined,
        speaker: undefined,
      };
    }
    if (audio) {
      const audioDevices = await this.#localMediaUtils.getAudioDevices();
      if (audioDevices.length !== 0) {
        const audioDeviceId = (await this.getOperationalMicDeviceId()).deviceId;
        this.currentDevices.audio = this.#localMediaUtils.getDeviceById(
          audioDeviceId,
          'audioinput'
        );
        constraints.audio = true;
      }
    }

    if (video) {
      const videoDevices = await this.#localMediaUtils.getVideoDevices();
      if (videoDevices.length !== 0) {
        const videoDeviceId = (await this.getOperationalWebcamDeviceId())
          .deviceId;
        this.currentDevices.video = this.#localMediaUtils.getDeviceById(
          videoDeviceId,
          'videoinput'
        );

        const videoConstraints = LocalMediaUtils.getVideoConstraints({
          videoDeviceId,
        });
        constraints.video = videoConstraints.video;
      }
    }

    if (audio) await this.setupAudioStream();
    if (video) await this.setupVideoStream();

    if (this.audioTrack) this.audioEnabled = true;
    else this.audioEnabled = false;

    if (this.videoTrack) this.videoEnabled = true;
    else this.videoEnabled = false;

    this.screenShareEnabled = false;

    // Setting default audio output to speaker
    await this.setupSpeaker();

    // Setup Foreground Service after handling permissions
    if (Platform.OS === 'android') {
      this.#appStateSubscription = AppState.addEventListener(
        'change',
        this._handleAppStateChange
      );
    }
  }

  private stopScreenShareTracks(
    tracks: { audio?: boolean; video?: boolean } = {}
  ) {
    const { audio = true, video = true } = tracks;
    if (audio) {
      this.screenShareTracks?.audio?.stop();
    }

    if (video) {
      this.screenShareTracks?.video?.stop();
    }

    if (audio && video) {
      this.screenShareEnabled = false;
    }
  }

  public removeScreenShareTracks(tracks?: {
    audio?: boolean;
    video?: boolean;
  }) {
    this.screenShareTracks.video?.removeEventListener(
      'ended',
      this.onScreenShareEnded
    );
    this.stopScreenShareTracks(tracks);
    this.screenShareTracks = {
      audio: undefined,
      video: undefined,
    };
  }

  private setAudioTrack(audioTrack: MediaStreamTrack) {
    if (audioTrack === this.audioTrack) {
      return;
    }

    if (audioTrack === undefined) {
      this.rawAudioTrack?.stop();
      this.rawAudioTrack?.release();
      this.rawAudioTrack = undefined;
    }

    this.audioTrack = audioTrack;
  }

  private setVideoTrack(videoTrack: MediaStreamTrack) {
    if (videoTrack === this.videoTrack) {
      return;
    }

    if (videoTrack === undefined) {
      this.rawVideoTrack?.stop();
      this.rawVideoTrack?.release();
      this.rawVideoTrack = undefined;
    }

    this.videoTrack = videoTrack;
  }

  private setScreenShareTracks(tracks: {
    audio?: MediaStreamTrack;
    video?: MediaStreamTrack;
  }) {
    const { audio, video } = tracks;
    if (!audio || !video) {
      this.stopScreenShareTracks({ audio: !!audio, video: !!video });
    }

    this.screenShareTracks = tracks;
    this.screenShareTracks.video?.addEventListener(
      'ended',
      this.onScreenShareEnded.bind(this)
    );
  }

  private onScreenShareEnded() {
    this.emit('SCREENSHARE_ENDED');
  }

  public async disableAudio() {
    await this.toggleAudio();
  }

  public async enableAudio() {
    await this.toggleAudio();
  }

  public async toggleAudio() {
    if (this.permissions.audio !== 'ACCEPTED') {
      try {
        const permResult = await setupPermissions(true, false);
        this.permissions.audio = permResult.microphone;
      } catch (val) {
        this.permissions.audio = val.microphone ?? false;
      }
    }
    this.emit('MEDIA_PERMISSION_UPDATE', {
      kind: 'audio',
      message: this.permissions.audio,
    });
    if (
      this.permissions.audio === 'DENIED' ||
      this.permissions.audio === 'NOT_REQUESTED'
    ) {
      this.audioEnabled = false;
      return;
    }
    if (this.audioEnabled) {
      this.audioEnabled = false;

      if (this.audioTrack) {
        this.audioTrack.enabled = this.audioEnabled;
      }
      return;
    }
    try {
      /**
       * NOTE (@madhugb): We need to setup new stream,
       * if the audio track is not preset or it has ended
       */
      if (!this.audioTrack || this.audioTrack.readyState === 'ended') {
        await this.setupAudioStream(this.currentDevices.audio?.deviceId);
      }
      // if we still can't get a track after setting up audio stream
      // do not toggle the audio state
      if (!this.audioTrack) {
        this.audioEnabled = false;
        return;
      }
      this.audioEnabled = true;
    } catch (ex) {
      this.audioEnabled = false;
      this.audioTrack = undefined;
      this.rawAudioTrack = undefined;
    }
    if (this.audioTrack) {
      this.audioTrack.enabled = true;
    }
  }

  public async enableVideo() {
    await this.toggleVideo();
  }

  public async disableVideo() {
    await this.toggleVideo();
  }

  public async toggleVideo() {
    if (this.permissions.video !== 'ACCEPTED') {
      try {
        const permResult = await setupPermissions(false, true);
        this.permissions.video = permResult.camera;
      } catch (val) {
        this.permissions.video = val.camera ?? false;
      }
    }
    this.emit('MEDIA_PERMISSION_UPDATE', {
      kind: 'video',
      message: this.permissions.video,
    });
    if (
      this.permissions.video === 'DENIED' ||
      this.permissions.video === 'NOT_REQUESTED'
    ) {
      this.videoEnabled = false;
      return;
    }
    if (this.videoEnabled) {
      this.rawVideoTrack?.stop();
      this.rawVideoTrack?.release();
      this.rawVideoTrack = undefined;
      this.videoEnabled = false;
      return;
    }
    /**
     * NOTE(roerohan): The videoEnabled flag is used inside the setupVideoStream method
     * hence it must be set to true first.
     */
    this.videoEnabled = true;
    await this.setupVideoStream(this.currentDevices.video?.deviceId);
  }

  public async setAudioDevice(device: MediaDeviceInfo) {
    await this.setupAudioStream(device.deviceId);
  }

  public async setVideoDevice(device: MediaDeviceInfo) {
    if (this.videoEnabled) {
      this.rawVideoTrack?.stop();
      this.rawVideoTrack?.release();
      this.rawVideoTrack = undefined;
    }
    await this.setupVideoStream(device.deviceId);
  }

  public async setSpeakerDevice(device?: MediaDeviceInfo) {
    await this.setupSpeaker(device.deviceId);
  }

  public async setDevice(route: MediaDeviceInfo) {
    await this.#localMediaUtils.chooseAudioRoute(route.deviceId);
  }

  private async oniOSBroadcastStart() {
    this.setScreenShareTracks(await LocalMediaUtils.getScreenShareTracks());
    this.screenShareEnabled = true;
    this.#screenShareState = 'STARTED';
  }

  private async oniOSBroadcastStop() {
    this.#screenShareState = 'STOPPED';
    if (this.screenShareEnabled) {
      this.removeScreenShareTracks();
      return;
    }
  }

  public async toggleScreenShare() {
    if (this.screenShareEnabled && Platform.OS !== 'ios') {
      this.removeScreenShareTracks();
      try {
        const val = await RTKHelper.isForegroundServiceRunning();
        if (val) {
          await RTKHelper.stopService();
        }
      } catch (e) {
        console.warn('Failed to stop foreground service', e);
      }
      return;
    }
    if (Platform.OS === 'ios' && this.screenShareEnabled) {
      NativeModules.RTKScreensharePickerView.showScreenSharePickerView();
      this.#screenShareState = 'PENDING';
      await new Promise<void>((resolve, reject) => {
        let timeoutId: NodeJS.Timeout;
        let time: number = 0;
        timeoutId = setInterval(() => {
          if (this.#screenShareState === 'STOPPED') {
            clearInterval(timeoutId);
            resolve();
          }
          time = time + 100;
          if (time > 20000) {
            clearInterval(timeoutId);
            reject('Timeout exceeded for disableScreenShare()');
          }
        }, 100);
      });

      return;
    }

    if (Platform.OS === 'android') {
      this._pendingScreenShare = true;
      await this.configureForeground();
      try {
        this.setScreenShareTracks(await LocalMediaUtils.getScreenShareTracks());
        this.screenShareEnabled = true;
      } catch (e) {
        console.warn('Failed to start screenshare', e);
        this.screenShareEnabled = false;
        const val = await RTKHelper.isForegroundServiceRunning();
        if (val) {
          await RTKHelper.stopService();
        }
        this._pendingScreenShare = false;
      } finally {
        this._pendingScreenShare = false;
      }
    }
    if (Platform.OS === 'ios') {
      this.#screenShareState = 'PENDING';
      NativeModules.RTKScreensharePickerView.showScreenSharePickerView();
      await new Promise<void>((resolve, reject) => {
        let timeoutId: NodeJS.Timeout;
        let time: number = 0;
        timeoutId = setInterval(() => {
          if (this.#screenShareState === 'STARTED') {
            clearInterval(timeoutId);
            resolve();
          }
          time = time + 100;
          if (time > 20000) {
            clearInterval(timeoutId);
            reject('Timeout exceeded for enableScreenShare()');
          }
        }, 100);
      });
    }
  }

  public async enableScreenShare() {
    await this.toggleScreenShare();
  }

  public async disableScreenShare() {
    await this.toggleScreenShare();
  }

  public getAllDevices() {
    return this.#localMediaUtils.availableDevices;
  }

  public getCurrentDevices() {
    return this.currentDevices;
  }

  public getDeviceById(
    deviceId: string,
    kind: 'audioinput' | 'audiooutput' | 'videoinput'
  ) {
    return this.#localMediaUtils.getDeviceById(deviceId, kind);
  }

  public async getAudioDevices() {
    const devices = await this.#localMediaUtils.getAudioDevices();

    // Update current audio device from native layer
    try {
      const currentDeviceId = await NativeAudioManager.getCurrentAudioDevice();
      if (currentDeviceId) {
        const currentDevice = this.#localMediaUtils.getDeviceById(
          currentDeviceId,
          'audioinput'
        );
        if (currentDevice) {
          this.currentDevices.audio = currentDevice;
        }
      }
    } catch (e) {
      console.warn('Failed to update current audio device:', e);
    }

    return devices;
  }

  public async getVideoDevices() {
    return await this.#localMediaUtils.getVideoDevices();
  }

  public async getSpeakerDevices() {
    const devices = await this.#localMediaUtils.getSpeakerDevices();

    // Update current speaker device from native layer
    try {
      const currentDeviceId = await NativeAudioManager.getCurrentAudioDevice();
      if (currentDeviceId) {
        const currentDevice = this.#localMediaUtils.getDeviceById(
          currentDeviceId,
          'audioinput'
        );
        if (currentDevice) {
          this.currentDevices.speaker = currentDevice;
          this.currentDevices.audio = currentDevice;
        }
      }
    } catch (e) {
      console.warn('Failed to update current speaker device:', e);
    }

    return devices;
  }

  public async onDeviceChange(
    changedDevices: {
      added: MediaDeviceInfo[];
      removed: MediaDeviceInfo[];
      selectedDevice?: string;
    },
    forceDeviceChange: boolean
  ) {
    let addedDevice: MediaDeviceInfo;
    let removedDevice: MediaDeviceInfo;
    if (changedDevices.added.length) [addedDevice] = changedDevices.added;

    if (changedDevices.removed.length) [removedDevice] = changedDevices.removed;

    // Update currentDevices.speaker when platform reports selected device change
    if (changedDevices.selectedDevice) {
      const selectedDeviceInfo = this.#localMediaUtils.getDeviceById(
        changedDevices.selectedDevice,
        'audioinput'
      );

      if (selectedDeviceInfo) {
        this.currentDevices.speaker = selectedDeviceInfo;
        this.currentDevices.audio = selectedDeviceInfo;
      }
    }

    /**
     * NOTE(roerohan): forceStopTrack is set required to be set to true for firefox.
     * https://stackoverflow.com/questions/59068146/navigator-mediadevices-getusermedia-api-rejecting-with-error-notreadableerror
     * forceStopTrack is the second argument of setupAudioStream and setupVideoStream.
     */
    if (addedDevice) {
      // Clear any pending device removal timeout when a device is added
      if (this.#deviceRemovalTimeout) {
        clearTimeout(this.#deviceRemovalTimeout);
        this.#deviceRemovalTimeout = null;
      }

      if (addedDevice.kind === 'audioinput') {
        if (
          this.permissions.audio === 'DENIED' ||
          this.permissions.audio === 'NOT_REQUESTED'
        ) {
          await this.setupSpeaker(addedDevice.deviceId);
        } else {
          await this.setupAudioStream(addedDevice.deviceId);
        }
      } else if (addedDevice.kind === 'videoinput') {
        await this.setupVideoStream(addedDevice.deviceId);
      } else if (addedDevice.kind === 'audiooutput') {
        await this.setupSpeaker(addedDevice.deviceId);
      }
      this.emit('AUDIO_TRACK_CHANGE');
    }

    // Only handle device removal if no device was added
    // This prevents switching away from newly connected devices (e.g., Bluetooth)
    if (removedDevice && !addedDevice) {
      // For Bluetooth devices, add a delay to handle rapid reconnections
      // (e.g., when AirPods are put in ears, they briefly disconnect then reconnect)
      const isBluetoothRemoved = removedDevice.deviceId === 'bluetooth';

      const handleRemoval = async () => {
        if (removedDevice.kind === 'audioinput') {
          if (
            this.permissions.audio === 'DENIED' ||
            this.permissions.audio === 'NOT_REQUESTED'
          ) {
            await this.setupSpeaker();
          } else {
            await this.setupAudioStream();
          }
        } else if (removedDevice.kind === 'videoinput') {
          await this.setupVideoStream();
        } else if (removedDevice.kind === 'audiooutput') {
          await this.setupSpeaker();
        }
        this.emit('AUDIO_TRACK_CHANGE');
      };

      if (isBluetoothRemoved) {
        // Wait 500ms before switching away from Bluetooth to allow for reconnection
        this.#deviceRemovalTimeout = setTimeout(handleRemoval, 500);
      } else {
        // For other devices, handle removal immediately
        await handleRemoval();
      }
    }

    // Emit DEVICE_LIST_UPDATED if:
    // 1. Devices were added or removed, OR
    // 2. Selected device changed (platform routed to a different device)
    const hasDeviceListChanged =
      changedDevices.added.length || changedDevices.removed.length;
    const hasSelectedDeviceInfo = changedDevices.selectedDevice.length > 0;

    if (hasDeviceListChanged || hasSelectedDeviceInfo) {
      this.emit('DEVICE_LIST_UPDATED', changedDevices);
    }
  }

  public static async init(_) {
    const localMediaUtils = await LocalMediaUtils.init();
    return new LocalMediaHandler(localMediaUtils);
  }

  public emit(event: keyof typeof MediaEvents, ...args: any[]) {
    return super.emit(event, ...args);
  }

  public on(
    event: keyof typeof MediaEvents,
    listener: (...args: any[]) => void
  ) {
    return super.on(event, listener);
  }

  public destruct() {
    this.stopAudioTrack();
    this.stopVideoTrack();
    this.stopScreenShareTracks();
    if (Platform.OS === 'android') this.#appStateSubscription?.remove();
  }

  private stopAudioTrack() {
    this.rawAudioTrack?.stop();
    this.audioTrack?.stop();
    this.rawAudioTrack?.release();
    this.audioTrack?.release();
    this.audioEnabled = false;
    this.audioTrack = undefined;
    this.rawAudioTrack = undefined;
  }

  private stopVideoTrack() {
    this.rawVideoTrack?.stop();
    this.videoTrack?.stop();
    this.rawVideoTrack?.release();
    this.videoTrack?.release();
    this.videoTrack = undefined;
    this.rawVideoTrack = undefined;
  }
}

export interface RNLocalMediaHandler {
  new (): LocalMediaHandler;
}
